import { App } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { BodyOf } from "@/shared/api";
import { apiPatch, apiUpload, errorMessage, qk } from "@/shared/api";
import type { paths } from "@/api/types";

export type SettingsBody = BodyOf<paths["/api/v1/admin/settings"]["patch"]>;

export function useEditSettings() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (body: SettingsBody) => apiPatch("/api/v1/admin/settings", { body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.settings() });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (error) => {
      void message.error(errorMessage(error));
    },
  });
}

/**
 * Загрузка баннера ежедневной рассылки.
 *
 * Отдельная мутация, а не поле формы настроек: файл уходит multipart-ручкой, и
 * в JSON-теле PATCH его не передать. Снимается баннер, наоборот, обычным
 * PATCH — `{ broadcast_image_key: null }`, тем же приёмом, что и картинка
 * робота: непустое значение приняли бы за ключ, которого в бакете нет.
 */
export function useUploadBroadcastImage() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (file: File) =>
      apiUpload<unknown>("/api/v1/admin/settings/broadcast-image", { file }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.settings() });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (error) => {
      void message.error(errorMessage(error));
    },
  });
}
